import { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedPermissions = localStorage.getItem('permissions');
    const expiresAt = localStorage.getItem('token_expires_at');

    if (storedToken && storedUser && storedUser !== 'undefined') {
      if (expiresAt && new Date(expiresAt) < new Date()) {
        clearAuth();
        setLoading(false);
        return;
      }

      // Refresh from server to get latest user state
      authAPI.getUser().then((res) => {
        const freshUser = res?.data;
        if (freshUser) {
          const { permissions: perms, teacher_id: _, ...userData } = freshUser;
          setToken(storedToken);
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
          if (perms) {
            setPermissions(perms);
            localStorage.setItem('permissions', JSON.stringify(perms));
          } else if (storedPermissions && storedPermissions !== 'undefined') {
            setPermissions(JSON.parse(storedPermissions));
          }
        } else {
          clearAuth();
        }
      }).catch(() => {
        clearAuth();
      }).finally(() => {
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, []);

  const login = (userData, authToken, userPermissions, expiresAt) => {
    setUser(userData);
    setToken(authToken);
    setPermissions(userPermissions || {});
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('permissions', JSON.stringify(userPermissions || {}));
    localStorage.setItem('token_expires_at', expiresAt);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const updateUser = (userData) => {
    setUser({ ...userData });
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const clearAuth = () => {
    setUser(null);
    setToken(null);
    setPermissions({});
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('token_expires_at');
  };

  const hasPermission = useCallback((resource, action) => {
    return permissions[resource]?.includes(action) || false;
  }, [permissions]);

  const canRead = useCallback((resource) => hasPermission(resource, 'read'), [hasPermission]);
  const canCreate = useCallback((resource) => hasPermission(resource, 'create'), [hasPermission]);
  const canUpdate = useCallback((resource) => hasPermission(resource, 'update'), [hasPermission]);
  const canDelete = useCallback((resource) => hasPermission(resource, 'delete'), [hasPermission]);
  const canExport = useCallback((resource) => hasPermission(resource, 'export'), [hasPermission]);

  const refreshPermissions = useCallback(async (fresh) => {
    try {
      const perms = fresh ?? (await authAPI.getUser())?.data?.permissions;
      if (perms) {
        setPermissions(perms);
        localStorage.setItem('permissions', JSON.stringify(perms));
      }
    } catch {
      // silent
    }
  }, []);

  return (
    <AuthContext.Provider value={{
      user, token, permissions, loading,
      login, logout, updateUser, refreshPermissions,
      hasPermission, canRead, canCreate, canUpdate, canDelete, canExport,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
